import { Filter } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FilterChip } from '@/components/FilterChip';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useLeadFilters } from '@/hooks/useLeadFilters.ts';
import { cn } from '@/lib/utils';
import type {
  ContactabilityFilterCount,
  FilterChipCount,
  UrgencyFilterCount,
} from '@/models/ui/filterChip';
import {
  clearLeadFilterParams,
  readLeadFiltersFromSearchParams,
  toggleContactabilityFilter,
  toggleStatusFilter,
  toggleUrgencyFilter,
  writeLeadFiltersToSearchParams,
} from '@/models/ui/leadFilterSearchParams.ts';
import type { LeadFilters } from '@/models/ui/leadFilters.ts';

interface QuickFiltersProps {
  readonly filterChipCounts: FilterChipCount[];
  readonly urgencyFilterCounts: UrgencyFilterCount[];
  readonly contactabilityFilterCounts: ContactabilityFilterCount[];
}

const navClasses = cn(
  'bg-white/40 backdrop-blur-sm rounded-lg p-2 mb-2 border border-gray-200/50 shadow-sm',
  'flex items-start gap-3 w-full',
);

const iconWrapperClasses =
  'mt-1 flex items-center justify-center shrink-0 opacity-50';
const iconClasses = 'w-3.5 h-3.5 text-gray-700';

export function QuickFilters({
  filterChipCounts,
  urgencyFilterCounts,
  contactabilityFilterCounts,
}: QuickFiltersProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const filters = useLeadFilters();

  const applyFilters = (updater: (current: LeadFilters) => LeadFilters) => {
    setSearchParams(
      (currentSearchParams) => {
        const currentFilters =
          readLeadFiltersFromSearchParams(currentSearchParams);
        const nextFilters = updater(currentFilters);
        return writeLeadFiltersToSearchParams(currentSearchParams, nextFilters);
      },
      { replace: true },
    );
  };

  const clearAllFilters = () => {
    const nextParams = clearLeadFilterParams(searchParams);
    setSearchParams(nextParams, { replace: true });
  };

  const hasSelectedFilters =
    filters.statuses.length > 0 ||
    filters.urgencies.length > 0 ||
    filters.contactabilities.length > 0 ||
    filters.search.length > 0;

  const activeChipFilterCount =
    filters.statuses.length +
    filters.urgencies.length +
    filters.contactabilities.length;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            navClasses,
            'justify-start transition-colors',
            isOpen && 'border-[#4A4741]/70 bg-[#4A4741]/10',
          )}
          aria-expanded={isOpen}
        >
          <span className={iconWrapperClasses}>
            <Filter
              className={cn(
                iconClasses,
                isOpen && 'text-[#4A4741] opacity-100',
                !isOpen && hasSelectedFilters && 'text-[#4A4741] opacity-100',
              )}
            />
          </span>
          <span>Filters</span>
          {activeChipFilterCount > 0 ? (
            <span className="ml-auto text-xs font-medium text-[#4A4741]">
              {activeChipFilterCount} active
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        className="w-[28rem] max-w-[calc(100vw-2rem)]"
      >
        <div className="mb-3 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-[#272623]">Filters</h4>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-1 text-xs"
            onClick={clearAllFilters}
            disabled={!hasSelectedFilters}
          >
            Clear filters
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-[#272623]">Status</h4>
            <ul className="space-y-1.5">
              {filterChipCounts.map((filterChipCount) => (
                <FilterChip
                  key={filterChipCount.status}
                  filterChipCount={filterChipCount}
                  isActive={filters.statuses.includes(filterChipCount.status)}
                  onToggle={(status) =>
                    applyFilters((current) =>
                      toggleStatusFilter(current, status),
                    )
                  }
                />
              ))}
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-[#272623]">Urgency</h4>
            <ul className="space-y-1.5">
              {urgencyFilterCounts.map((urgencyFilterCount) => (
                <FilterChip
                  key={urgencyFilterCount.urgency}
                  urgencyFilterCount={urgencyFilterCount}
                  isActive={filters.urgencies.includes(
                    urgencyFilterCount.urgency,
                  )}
                  onToggleUrgency={(urgency) =>
                    applyFilters((current) =>
                      toggleUrgencyFilter(current, urgency),
                    )
                  }
                />
              ))}
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-[#272623]">
              Contactability
            </h4>
            <ul className="space-y-1.5">
              {contactabilityFilterCounts.map((contactabilityFilterCount) => (
                <FilterChip
                  key={contactabilityFilterCount.contactability}
                  contactabilityFilterCount={contactabilityFilterCount}
                  isActive={filters.contactabilities.includes(
                    contactabilityFilterCount.contactability,
                  )}
                  onToggleContactability={(contactability) =>
                    applyFilters((current) =>
                      toggleContactabilityFilter(current, contactability),
                    )
                  }
                />
              ))}
            </ul>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
